import styled from '@emotion/styled';
import { Divider, Typography } from '@material-ui/core';
import { ClassAttributes, createElement, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { FormattedDate } from 'react-intl';

import {
  Creation,
  CreationState,
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatFastStart,
  CreativeFormatPlaybackTime,
  Deliverable,
} from '../../../graphql/generated/graphql';
import { toJSDate } from '../../../utility/graphql/datetime';
import DeliverableDetail from '../../component/compound/CreationDetail/DeliverableDetail';
import _InspectionModificationRequest from '../../component/compound/CreationDetail/InspectionModificationRequest';
import SubmissionForm from '../../component/compound/CreationDetail/SubmissionForm';
import { useCreateDeliverable } from '../../component/compound/CreationDetail/SubmissionForm/useCreateDeliverable';
import AvatarWithName from '../../component/primitive/AvatarWithName';
import PublicId from '../../component/primitive/PublicId';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';

interface Props {
  assignmentId: string;
  creation: Pick<Creation, 'assignee' | 'inspectionModificationRequests' | 'state'> & {
    deliverables: Pick<Deliverable, 'id' | 'creative' | 'attachedThumbnail' | 'intent' | 'createdAt'>[];
    creativeFormat: Pick<CreativeFormat, 'id'> & {
      creativeType: Pick<CreativeFormat['creativeType'], 'key' | 'name'>;
      attribute: {
        playbackTime?: {
          seconds: CreativeFormatPlaybackTime['seconds'];
        } | null;
        aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
        fastStart?: CreativeFormatFastStart | null;
      };
    };
  };
  hideForm: boolean;
  refresh: () => void;
  className?: string;
}

const SubmissionContent = ({
  assignmentId,
  creation,
  hideForm,
  className,
  refresh,
}: ClassAttributes<HTMLElement> & Props) => {
  const { createDeliverable, creating } = useCreateDeliverable(assignmentId, creation.creativeFormat.id);

  // case: under inspection, already submitted or some modification requests from director exist
  if (!creation.assignee) {
    return <div />;
  }
  const assignee = creation.assignee;
  const firstDeliverable = creation.deliverables[0] as Deliverable;
  if (creation.inspectionModificationRequests.length === 0) {
    return (
      <div className={className}>
        <TitleBar>
          <Title variant="h6">
            <FormattedMessage id="submission.title" /> #1
          </Title>
          <AvatarWithName imageUrl={assignee.profileImageUrl} name={assignee.name} small />
          <UpdateDateTime>
            <FormattedDate
              year="numeric"
              month="long"
              day="2-digit"
              hour="2-digit"
              minute="2-digit"
              value={toJSDate(firstDeliverable.createdAt)}
            />
          </UpdateDateTime>
          <PublicId id={firstDeliverable.id} variant="caption" color="textSecondary" />
        </TitleBar>
        <DeliverableDetail
          creativeFormat={creation.creativeFormat}
          deliverable={firstDeliverable}
          type="submission"
          className={className}
        />
      </div>
    );
  } else {
    // case: inspection modification requests exist,
    // -> show mod request items and another submission form (or modificated deliverable)
    const inspectionModificationRequests = creation.inspectionModificationRequests.map(
      (inspectionModificationRequest, i) => {
        if (inspectionModificationRequest === null) {
          return null;
        }
        let submission;
        const modificatedDeliverableIndex = creation.deliverables.findIndex(
          (d): d is Deliverable => inspectionModificationRequest.targetDeliverableId === d?.id,
        );
        // index範囲外だとundefined
        const modificatedDeliverable = creation.deliverables[modificatedDeliverableIndex + 1];

        if (modificatedDeliverable) {
          submission = (
            <DeliverableDetail
              creativeFormat={creation.creativeFormat}
              deliverable={modificatedDeliverable}
              type="submission"
              className={className}
            />
          );
        } else {
          submission = hideForm ? (
            <Typography variant="caption">
              <FormattedMessage id="creation_state.canceled" />
            </Typography>
          ) : (
            <SubmissionForm
              creativeFormat={creation.creativeFormat}
              canceled={creation.state !== CreationState.Canceled}
              hasDeliverables={true}
              submit={createDeliverable}
              submitting={creating}
              onSubmit={refresh}
              type="submission"
            />
          );
        }

        return (
          <div key={inspectionModificationRequest.id}>
            <Divider />
            <InspectionModificationRequest inspectionModificationRequest={inspectionModificationRequest} index={i} />
            <SubSectionDivider />
            <Resubmission>
              <TitleBar>
                <Title variant="h6">
                  <FormattedMessage id="submission.title" /> #{i + 2}
                </Title>
                {modificatedDeliverable ? (
                  <Fragment>
                    <AvatarWithName imageUrl={assignee.profileImageUrl} name={assignee.name} small />
                    <UpdateDateTime>
                      <FormattedDate
                        year="numeric"
                        month="long"
                        day="2-digit"
                        hour="2-digit"
                        minute="2-digit"
                        value={toJSDate(modificatedDeliverable.createdAt)}
                      />
                    </UpdateDateTime>
                    <PublicId id={modificatedDeliverable.id} variant="caption" color="textSecondary" />
                  </Fragment>
                ) : null}
              </TitleBar>
              {submission}
            </Resubmission>
          </div>
        );
      },
    );

    return (
      <div className={className}>
        <TitleBar>
          <Title variant="h6">
            <FormattedMessage id="submission.title" /> #1
          </Title>
          <AvatarWithName imageUrl={creation.assignee.profileImageUrl} name={creation.assignee.name} small />
          <UpdateDateTime>
            <FormattedDate
              year="numeric"
              month="long"
              day="2-digit"
              hour="2-digit"
              minute="2-digit"
              value={toJSDate(firstDeliverable.createdAt)}
            />
          </UpdateDateTime>
          <PublicId id={firstDeliverable.id} variant="caption" color="textSecondary" />
        </TitleBar>
        <DeliverableDetail
          creativeFormat={creation.creativeFormat}
          deliverable={firstDeliverable}
          type="submission"
          className={className}
        />
        {inspectionModificationRequests}
      </div>
    );
  }
};

const TitleBar = styled('div')({
  ...flexibleLayoutContainer({ alignItems: 'center' }),
  marginBottom: SPACING_UNIT * 3,
});

const Title = styled(Typography)({ marginRight: SPACING_REGULAR });

const UpdateDateTime = styled(Typography)({
  marginLeft: SPACING_REGULAR,
  marginRight: SPACING_REGULAR,
  lineHeight: 1,
});
UpdateDateTime.defaultProps = { variant: 'caption' };

const InspectionModificationRequest = styled(_InspectionModificationRequest)({
  marginTop: SPACING_UNIT * 2,
  marginBottom: SPACING_UNIT * 3,
});

const Resubmission = styled('div')({ marginTop: SPACING_UNIT * 3 });

const SubSectionDivider = styled(Divider)({ marginLeft: -SPACING_UNIT * 3, marginRight: -SPACING_UNIT * 3 });

export default SubmissionContent;
